/* eslint-disable eqeqeq */
import { useState, useEffect } from "react";

interface PricingCalculatorProps {
  id: number;
  title: string;
  image: string;
  color: string;
  qty: { [color: string]: number }[];
  price: number;
  handleDeleteProduct: (id: number) => void;
  discountPercentage?: number;
}

const PricingCalculator = ({
  id,
  title,
  image,
  color,
  qty,
  price,
  handleDeleteProduct,
  discountPercentage = 0,
}: PricingCalculatorProps) => {
  const [quantity, setQuantity] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [savedAmount, setSavedAmount] = useState(0);
  const [discountedPricePerItem, setDiscountedPricePerItem] = useState(0);

  // Calculate total quantity and total price
  useEffect(() => {
    const totalQty = qty.reduce(
      (total, item: any) =>
        total + parseInt(item.quantity ? item.quantity.selected : 0),
      0
    );
    setQuantity(totalQty);
    
    // Calculate original price
    const original = totalQty * price;
    setOriginalPrice(original);
    
    // Apply discount if available
    const discounted = original * (1 - discountPercentage);
    setTotalPrice(discounted);
    
    // Calculate amount saved
    const saved = original - discounted;
    setSavedAmount(saved);
    
    // Calculate discounted price per item
    if (totalQty > 0) {
      setDiscountedPricePerItem(discounted / totalQty);
    }
  }, [qty, price, discountPercentage]);

  return (
    <>
      <div className="flex flex-col my-6 pt-5 border-t border-solid border-gray-200 rounded-md overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300">
        {/* Product Header */}
        <div className="flex gap-5 justify-between px-4 pb-4">
          <div className="flex gap-4">
            <div className="relative">
              <img src={image} alt={title} className="max-w-[60px] max-h-fit rounded-md shadow-sm object-cover" />
              {discountPercentage > 0 && (
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center shadow-sm">
                  {(discountPercentage * 100).toFixed(0)}%
                </div>
              )}
            </div>
            <div className="max-w-[290px]">
              <div className="font-bold text-gray-800">{title}</div>
              <div className="text-sm mt-1">
                <span className="text-gray-500">Color:</span> <span className="font-semibold text-gray-700">{color}</span>
              </div>

              {/* Sizes section commented out as requested */}
              {/* 
              {qty.length > 0 && (
                <div className="text-sm mt-1">
                  <span className="text-gray-500">Sizes:</span>{" "}
                  <span className="font-semibold uppercase text-gray-700">
                    {qty
                      .filter((item: any) => item.quantity.selected > 0)
                      .map((item: any) => {
                        return `${item.size}-${item.quantity.selected}`;
                      })
                      .join(", ")}
                  </span>
                </div>
              )}
              */}
            </div>
          </div>

          {/* Delete Button */}
          <div className="pt-1">
            <button 
              onClick={() => handleDeleteProduct(id)}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <svg
                className="cursor-pointer"
                width="15"
                height="18"
                viewBox="0 0 15 18"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.997685 5.07662V17.5242H13.4453V5.07662H0.997685ZM4.45535 14.7581C4.45535 15.1398 4.14554 15.4496 3.76381 15.4496C3.38209 15.4496 3.07228 15.1398 3.07228 14.7581V7.84275C3.07228 7.46103 3.38209 7.15122 3.76381 7.15122C4.14554 7.15122 4.45535 7.46103 4.45535 7.84275V14.7581ZM7.91301 14.7581C7.91301 15.1398 7.6032 15.4496 7.22147 15.4496C6.83975 15.4496 6.52994 15.1398 6.52994 14.7581V7.84275C6.52994 7.46103 6.83975 7.15122 7.22147 7.15122C7.6032 7.15122 7.91301 7.46103 7.91301 7.84275V14.7581ZM11.3707 14.7581C11.3707 15.1398 11.0609 15.4496 10.6791 15.4496C10.2974 15.4496 9.9876 15.1398 9.9876 14.7581V7.84275C9.9876 7.46103 10.2974 7.15122 10.6791 7.15122C11.0609 7.15122 11.3707 7.46103 11.3707 7.84275V14.7581ZM14.1368 2.31049V3.69356H0.306152V2.31049H4.25549C4.87787 2.31049 5.38338 1.5505 5.38338 0.927429H9.05888C9.05888 1.5505 9.56369 2.31049 10.1868 2.31049H14.1368Z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Pricing Footer */}
        <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-between items-center">
          {/* Quantity and Price Per Item */}
          <div className="flex flex-col">
            <div className="font-medium text-gray-700">
              {quantity} item{quantity !== 1 && "s"}
            </div>
            
            {/* Price per item section with discount */}
            <div className="mt-1 flex items-center">
              {discountPercentage > 0 ? (
                <>
                  <div className="line-through text-gray-400 text-xs mr-2">${price.toFixed(2)}</div>
                  <div className="text-green-600 font-semibold">${discountedPricePerItem.toFixed(2)} each</div>
                </>
              ) : (
                <div>${price.toFixed(2)} each</div>
              )}
            </div>
          </div>
          
          {/* Total Price with Discount */}
          <div className="text-right">
            {discountPercentage > 0 ? (
              <div className="flex flex-col items-end">
                <div className="line-through text-gray-400 text-sm">${originalPrice.toFixed(2)}</div>
                <div className="text-green-600 text-xs font-medium mb-1">
                  {(discountPercentage * 100).toFixed(0)}% off (Save ${savedAmount.toFixed(2)})
                </div>
                <div className="font-bold text-base text-gray-800">
                  Total: <span className="text-green-700">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div className="font-bold text-base text-gray-800">
                Total: ${totalPrice.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingCalculator;
